import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout, Field } from "./AuthLayout";
import { PremiumInput } from "../../components/brand/PremiumInput";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../data/authStore";

const useQuery = () => new URLSearchParams(useLocation().search);

export default function AcceptInvitePage() {
  const q = useQuery();
  const token = q.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [fatal, setFatal] = useState("");

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setFatal("This invite link is missing a token.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/auth/invite/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setInfo(data);
      } catch (err) {
        if (cancelled) return;
        setFatal(apiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!info) return;
    if (!info.user_exists) {
      if (!fullName.trim()) return setError("Full name is required");
      if (!password || password.length < 8) return setError("Password must be at least 8 characters");
    } else if (!password || password.length < 8) {
      return setError("Password must be at least 8 characters");
    }
    setSubmitting(true);
    try {
      const body = { token };
      if (!info.user_exists) {
        body.full_name = fullName.trim();
        body.password = password;
      } else {
        body.password = password;
      }
      const { data } = await api.post("/api/auth/accept-invite", body);
      await refresh?.();
      toast.success(`Welcome to ${info.org_name}`, { description: `Joined as ${info.role_label}.` });
      nav("/today", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout testid="accept-invite-loading" eyebrow="Invite" title="Checking your invite…" subtitle="Hang tight while we verify the link.">
        <div className="flex items-center justify-center py-10 text-slate-500 text-[13px]">
          <Loader2 size={16} className="animate-spin mr-2" /> Verifying…
        </div>
      </AuthLayout>
    );
  }

  if (fatal || !info) {
    return (
      <AuthLayout
        testid="accept-invite-error"
        eyebrow="Invite"
        title="This invite isn't valid"
        subtitle={fatal || "The link may have expired or been revoked. Ask your admin to re-send it."}
        footer={<><Link to="/login" className="font-semibold text-indigo-700 hover:text-indigo-800" data-testid="invite-to-login">Sign in instead →</Link></>}
      >
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3" data-testid="accept-invite-fatal">
          <AlertTriangle size={16} className="text-rose-600 mt-0.5" />
          <p className="text-[12.5px] text-rose-800 leading-snug">{fatal || "Invite link is invalid."}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      testid="accept-invite-page"
      eyebrow="You're invited"
      title={`Join ${info.org_name}`}
      subtitle={`${info.inviter_name ? `${info.inviter_name} invited you` : "You've been invited"} to join as ${info.role_label}. ${info.user_exists ? "Sign in to accept." : "Set up your account to accept."}`}
      footer={<>Already have a different ChainFlow account? <Link to="/login" className="font-semibold text-indigo-700 hover:text-indigo-800" data-testid="invite-to-login">Sign in →</Link></>}
    >
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 px-3.5 py-3 mb-4 flex items-start gap-3" data-testid="accept-invite-summary">
        <ShieldCheck size={16} className="text-indigo-700 mt-0.5" />
        <div className="text-[12.5px] text-indigo-900 leading-snug">
          You'll join <span className="font-semibold">{info.org_name}</span> as <span className="font-semibold">{info.role_label}</span>.
          Email: <span className="font-semibold">{info.email}</span>.
        </div>
      </div>

      <form onSubmit={submit} data-testid="accept-invite-form" noValidate>
        {!info.user_exists && (
          <Field label="Full name">
            <PremiumInput
              type="text"
              autoComplete="name"
              size="lg"
              value={fullName}
              onChange={setFullName}
              placeholder="Olivia Marsh"
              testid="accept-invite-full-name"
              autoFocus
            />
          </Field>
        )}
        <Field label={info.user_exists ? "Confirm your password" : "Create a password"}>
          <PremiumInput
            type="password"
            autoComplete={info.user_exists ? "current-password" : "new-password"}
            size="lg"
            value={password}
            onChange={setPassword}
            placeholder="At least 8 characters"
            testid="accept-invite-password"
            autoFocus={info.user_exists}
          />
        </Field>
        {error && <p className="text-[12px] text-rose-600 -mt-1 mb-3" data-testid="accept-invite-error-msg">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className={`btn btn-primary !h-11 !px-5 !text-[13.5px] w-full ${submitting ? "opacity-60 cursor-not-allowed !shadow-none" : ""}`}
          data-testid="accept-invite-submit"
        >
          {submitting
            ? <><Loader2 size={14} className="animate-spin" /> Joining {info.org_name}…</>
            : <>Accept invite & continue <ArrowRight size={14} /></>}
        </button>
      </form>
    </AuthLayout>
  );
}
